/**
 * Guard global unique — portage de `app/middleware/auth.global.ts` (spec §8.3).
 *
 * > Le guard front est de l'ERGONOMIE, pas de la sécurité : la frontière reste
 * > `RoleGuard` + les gardes d'accès de Hello-Back.
 */
import type {
  LocationQuery, RouteLocation, RouteLocationRaw, RouteMeta, RouteRecordNameGeneric, Router,
} from 'vue-router';
import { AppRole } from '@/shared/enums';
import { useAuthStore } from '@/auth/authStore';
import { isAuthenticated, login } from '@/auth/keycloak';
import { cancelPendingConfirm } from '@/composables/useConfirm';

/** Ce que le guard lit d'une route — assez pour tester la matrice rôle × route. */
export interface GuardTarget {
  name?: RouteRecordNameGeneric;
  meta: RouteMeta;
  query?: LocationQuery;
  /** Posé par vue-router sur toute navigation redirigée (guard ou record `redirect`). */
  redirectedFrom?: Pick<RouteLocation, 'fullPath'>;
}

function boardName(role: AppRole): 'client-issues' | 'issues' {
  return role === AppRole.CLIENT ? 'client-issues' : 'issues';
}

/** Accueil par rôle : le client sur /issues, les internes sur /tickets (QP2). */
export function homeRedirect(role: AppRole): RouteLocationRaw {
  return { name: boardName(role) };
}

/** Lien de ticket recopié depuis le board de l'autre rôle : celui du lecteur, seul `?issue=` suit (ni `clientId` ni `view`). */
function readerBoard(role: AppRole, query: LocationQuery | undefined): RouteLocationRaw {
  const issue = query?.issue;
  return typeof issue === 'string' && issue !== ''
    ? { name: boardName(role), query: { issue } }
    : { name: boardName(role) };
}

/**
 * Décision pure du guard, une fois le rôle connu.
 * `true` = on laisse passer ; sinon, la destination de remplacement.
 */
export function resolveRouteAccess(to: GuardTarget, role: AppRole | null): true | RouteLocationRaw {
  if (role === null) return to.meta.allowNoRole ? true : { name: 'forbidden' };

  // Porteur de rôle gardé sur /forbidden après TOUTE redirection (⚠️ aucun `redirect` vers elle), sinon accueil en `replace`.
  if (to.meta.allowNoRole && to.name === 'forbidden') {
    return to.redirectedFrom ? true : { name: 'home', replace: true };
  }

  if (to.name === 'home') return homeRedirect(role);

  if (to.meta.roles && !to.meta.roles.includes(role)) {
    return to.name === 'issues' || to.name === 'client-issues' ? readerBoard(role, to.query) : { name: 'forbidden' };
  }

  return true;
}

export function installGuards(router: Router): void {
  router.beforeEach(async (to) => {
    if (to.meta.public) return true;

    if (!isAuthenticated()) {
      await login(window.location.origin + to.fullPath);
      return false; // navigation laissée en suspens : la page part sur Keycloak
    }

    const auth = useAuthStore();
    await auth.ensureMe();

    // ⚠️ Court-circuit AVANT la matrice de rôles : une panne technique de
    // `GET /api/me` (500, 502, réseau, session expirée) laisse `role` à null,
    // que `resolveRouteAccess` traduirait en « Accès refusé » — un diagnostic
    // faux, et dont la seule action proposée est la déconnexion. On laisse donc
    // passer la navigation : `App.vue` rend l'écran d'erreur global à la place
    // du layout, la page de destination n'est jamais montée (§5.2).
    if (auth.status === 'error') return true;

    return resolveRouteAccess(to, auth.role);
  });

  router.afterEach((to, from, failure) => {
    // Seul un changement de page compte : ?issue=, ?view=, ?clientId= ne touchent ni le titre ni la confirmation.
    if (failure || to.path === from.path) return;
    cancelPendingConfirm();
    document.title = to.meta.title ? `${to.meta.title} — Hello` : 'Hello';
  });
}
